<template>
  <section>
    <div class="titulo">
      <div class="margem">
        <h2>Metas de produção</h2>
      </div>
    </div>

    <div class="margem">
      <div class="kpi-abas" role="tablist" aria-label="Tipo de consulta">
        <button
          v-for="aba in abas"
          :key="aba.id"
          type="button"
          role="tab"
          class="kpi-abas__btn"
          :class="{ 'kpi-abas__btn--ativa': tipoAtivo === aba.id }"
          :aria-selected="tipoAtivo === aba.id ? 'true' : 'false'"
          @click="selecionarTipo(aba.id)"
        >
          {{ aba.label }}
        </button>
      </div>

      <div class="bloco margem">
        <h3 class="metas-secao-titulo">Parâmetros</h3>

        <!-- Insumos da meta -->
        <div v-if="tipoAtivo === 'insumos'" class="grid-3">
          <div class="autocomplete-wrapper">
            <label for="meta-prod-insumos">Produto *</label>
            <input
              id="meta-prod-insumos"
              type="text"
              v-model="productSearch"
              placeholder="Digite código ou nome…"
              @input="filterProducts"
              @focus="showDropdown = true"
              @blur="hideDropdown"
            />
            <ul v-if="showDropdown && filteredProducts.length" class="autocomplete-list">
              <li
                v-for="prod in filteredProducts"
                :key="prod.id"
                @mousedown.prevent="selectProduct(prod)"
              >
                {{ prod.code }} — {{ prod.name }}
              </li>
            </ul>
            <p v-if="produtoSelecionado" class="fonte-fraca fonte-menor margem">
              Selecionado: {{ produtoSelecionado.code }} (id {{ produtoSelecionado.id }})
            </p>
          </div>
          <div>
            <label for="meta-qt">Quantidade meta *</label>
            <input id="meta-qt" type="number" min="0.000001" step="any" v-model.number="qtMeta" />
          </div>
          <div>
            <label for="meta-modo">Modo *</label>
            <select id="meta-modo" v-model="modo">
              <option value="direto">Direto (filhos imediatos)</option>
              <option value="primario">Primário (só folhas)</option>
              <option value="arvore">Árvore completa</option>
            </select>
          </div>
          <div class="metas-check">
            <label>
              <input type="checkbox" v-model="considerarEstoque" />
              Considerar estoque
            </label>
          </div>
        </div>

        <!-- Produção por produto/ano -->
        <div v-else-if="tipoAtivo === 'produto_ano'" class="grid-3">
          <div class="autocomplete-wrapper">
            <label for="meta-prod-ano">Produto *</label>
            <input
              id="meta-prod-ano"
              type="text"
              v-model="productSearch"
              placeholder="Digite código ou nome…"
              @input="filterProducts"
              @focus="showDropdown = true"
              @blur="hideDropdown"
            />
            <ul v-if="showDropdown && filteredProducts.length" class="autocomplete-list">
              <li
                v-for="prod in filteredProducts"
                :key="prod.id"
                @mousedown.prevent="selectProduct(prod)"
              >
                {{ prod.code }} — {{ prod.name }}
              </li>
            </ul>
            <p v-if="produtoSelecionado" class="fonte-fraca fonte-menor margem">
              Selecionado: {{ produtoSelecionado.code }} (id {{ produtoSelecionado.id }})
            </p>
          </div>
          <div>
            <label for="meta-ano-prod">Ano *</label>
            <input id="meta-ano-prod" type="number" min="1000" max="9999" step="1" v-model.number="ano" />
          </div>
        </div>

        <!-- Produção por família/ano -->
        <div v-else class="grid-3">
          <div>
            <label for="meta-familia">Família *</label>
            <select id="meta-familia" v-model.number="familiaId">
              <option disabled :value="null">Selecione</option>
              <option v-for="f in familias" :key="f.id" :value="f.id">
                {{ f.cod ? `${f.cod} — ` : "" }}{{ f.nome }}
              </option>
            </select>
          </div>
          <div>
            <label for="meta-ano-fam">Ano *</label>
            <input id="meta-ano-fam" type="number" min="1000" max="9999" step="1" v-model.number="ano" />
          </div>
          <div class="metas-check">
            <label>
              <input type="checkbox" v-model="retornarProdutos" />
              Retornar produtos (detalhe)
            </label>
          </div>
        </div>

        <div class="linha alinha-v margem metas-acoes">
          <button type="button" :disabled="loading" @click="consultar">Consultar</button>
          <button type="button" class="acao-secundaria" :disabled="loading" @click="limparResultado">Limpar resultado</button>
        </div>
        <p v-if="erroValidacao" class="metas-erro">{{ erroValidacao }}</p>
      </div>

      <div v-if="apiError" class="bloco margem metas-erro" role="alert">{{ apiError }}</div>

      <div v-if="loading" class="margem alinha-centro">
        <div class="loading"><div></div></div>
        <p class="fonte-fraca margem">Consultando…</p>
      </div>

      <template v-else-if="resultado">
        <!-- Resumo produção anual -->
        <div v-if="tipoAtivo === 'produto_ano' || tipoAtivo === 'familia_ano'" class="bloco margem">
          <h3 class="metas-secao-titulo">Resumo</h3>
          <div class="grid-3">
            <article class="metas-tile">
              <div class="metas-tile__label">Ano</div>
              <div class="metas-tile__value">{{ resultado.ano }}</div>
            </article>
            <article class="metas-tile">
              <div class="metas-tile__label">Qtd. produzida</div>
              <div class="metas-tile__value">{{ formatNum(resultado.qt_produzida) }}</div>
            </article>
            <article class="metas-tile">
              <div class="metas-tile__label">OPs consideradas</div>
              <div class="metas-tile__value">{{ resultado.ops_consideradas }}</div>
            </article>
          </div>
          <p v-if="resultado.produto" class="fonte-fraca margem">
            Produto: {{ resultado.produto.cod }} — {{ resultado.produto.desc }}
          </p>
          <p v-if="resultado.familia" class="fonte-fraca margem">
            Família: {{ resultado.familia.familia_nome }}
            <span v-if="resultado.familia.familia_cod"> ({{ resultado.familia.familia_cod }})</span>
          </p>
        </div>

        <!-- Detalhe insumos -->
        <div v-if="tipoAtivo === 'insumos'" class="bloco margem">
          <h3 class="metas-secao-titulo">Insumos</h3>
          <p class="fonte-fraca">
            Meta: {{ formatNum(resultado.qt_meta, resultado.produto?.unidade) }} · Modo: {{ resultado.modo }}
            <span v-if="resultado.produto">
              · {{ resultado.produto.cod }} — {{ resultado.produto.desc }}
            </span>
          </p>

          <div v-if="resultado.modo === 'arvore'" class="margem">
            <table class="tabela">
              <thead>
                <tr>
                  <th>Código</th>
                  <th>Descrição</th>
                  <th>Nível</th>
                  <th>Qt. unitária</th>
                  <th>Qt. necessária</th>
                  <th>Folha</th>
                  <th v-if="resultado.considerar_estoque">Estoque</th>
                  <th v-if="resultado.considerar_estoque">Disponível</th>
                  <th v-if="resultado.considerar_estoque">A adquirir</th>
                  <th>Unidade</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, idx) in arvoreFlat" :key="`${row.produto_cod}-${idx}`">
                  <td>{{ row.cod }}</td>
                  <td>
                    <span :style="{ paddingLeft: `${row.nivel * 12}px` }">{{ row.desc }}</span>
                  </td>
                  <td>{{ row.nivel }}</td>
                  <td>{{ formatNum(row.qt_unitaria, row.unidade) }}</td>
                  <td>{{ formatNum(row.qt_necessaria, row.unidade) }}</td>
                  <td>{{ row.folha ? "Sim" : "Não" }}</td>
                  <td v-if="resultado.considerar_estoque">{{ formatNum(row.estoque, row.unidade) }}</td>
                  <td v-if="resultado.considerar_estoque">{{ formatNum(row.disponivel, row.unidade) }}</td>
                  <td v-if="resultado.considerar_estoque">{{ formatNum(row.a_adquirir, row.unidade) }}</td>
                  <td>{{ row.unidade || "—" }}</td>
                </tr>
                <tr v-if="!arvoreFlat.length">
                  <td :colspan="resultado.considerar_estoque ? 10 : 7" class="fonte-fraca">Nenhum item na estrutura.</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div v-else class="margem">
            <table class="tabela">
              <thead>
                <tr>
                  <th>Código</th>
                  <th>Descrição</th>
                  <th>Qt. unitária</th>
                  <th>Qt. necessária</th>
                  <th v-if="resultado.considerar_estoque">Estoque</th>
                  <th v-if="resultado.considerar_estoque">Disponível</th>
                  <th v-if="resultado.considerar_estoque">A adquirir</th>
                  <th>Unidade</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in resultado.itens || []" :key="item.produto_cod">
                  <td>{{ item.cod }}</td>
                  <td>{{ item.desc }}</td>
                  <td>{{ formatNum(item.qt_unitaria ?? item.qt_unitaria_efetiva, item.unidade) }}</td>
                  <td>{{ formatNum(item.qt_necessaria, item.unidade) }}</td>
                  <td v-if="resultado.considerar_estoque">{{ formatNum(item.estoque, item.unidade) }}</td>
                  <td v-if="resultado.considerar_estoque">{{ formatNum(item.disponivel, item.unidade) }}</td>
                  <td v-if="resultado.considerar_estoque">{{ formatNum(item.a_adquirir, item.unidade) }}</td>
                  <td>{{ item.unidade || "—" }}</td>
                </tr>
                <tr v-if="!(resultado.itens || []).length">
                  <td :colspan="resultado.considerar_estoque ? 8 : 5" class="fonte-fraca">Nenhum item na estrutura.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Produtos da família -->
        <div v-if="tipoAtivo === 'familia_ano' && Array.isArray(resultado.produtos)" class="bloco margem">
          <h3 class="metas-secao-titulo">Produtos da família</h3>
          <table class="tabela">
            <thead>
              <tr>
                <th>Código</th>
                <th>Descrição</th>
                <th>Qtd. produzida</th>
                <th>OPs</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="p in resultado.produtos" :key="p.produto_cod">
                <td>{{ p.cod }}</td>
                <td>{{ p.desc }}</td>
                <td>{{ formatNum(p.qt_produzida) }}</td>
                <td>{{ p.ops_consideradas }}</td>
              </tr>
              <tr v-if="!resultado.produtos.length">
                <td colspan="4" class="fonte-fraca">Nenhum produto com produção no ano.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>
    </div>
  </section>
</template>

<script>
import ProductDataSource from "@/data/datasource/ProductDataSource";
import FamiliaProdutoDataSource from "@/data/datasource/FamiliaProdutoDataSource";
import MetasProducaoDatasource from "@/data/datasource/MetasProducaoDatasource";

const TIPOS = {
  insumos: "insumos",
  produto_ano: "produto_ano",
  familia_ano: "familia_ano",
};

export default {
  name: "MetasProducao",
  data() {
    const anoPadrao = new Date().getFullYear() - 1;
    return {
      abas: [
        { id: TIPOS.insumos, label: "Insumos da meta" },
        { id: TIPOS.produto_ano, label: "Produção por produto" },
        { id: TIPOS.familia_ano, label: "Produção por família" },
      ],
      tipoAtivo: TIPOS.insumos,
      loading: false,
      apiError: null,
      erroValidacao: null,
      resultado: null,

      products: [],
      productSearch: "",
      filteredProducts: [],
      showDropdown: false,
      produtoSelecionado: null,

      qtMeta: 1,
      modo: "direto",
      considerarEstoque: false,

      ano: anoPadrao,
      familias: [],
      familiaId: null,
      retornarProdutos: false,
    };
  },
  computed: {
    arvoreFlat() {
      if (!this.resultado?.arvore) return [];
      return this.flattenArvore(this.resultado.arvore, 0);
    },
  },
  async mounted() {
    await Promise.all([this.carregarProdutos(), this.carregarFamilias()]);
  },
  methods: {
    async carregarProdutos() {
      try {
        this.products = await ProductDataSource.getProducts();
      } catch (e) {
        console.error(e);
        this.products = [];
      }
    },
    async carregarFamilias() {
      try {
        this.familias = await FamiliaProdutoDataSource.listar();
      } catch (e) {
        console.error(e);
        this.familias = [];
      }
    },
    selecionarTipo(id) {
      if (this.tipoAtivo === id) return;
      this.tipoAtivo = id;
      this.limparResultado();
      this.erroValidacao = null;
    },
    filterProducts() {
      const q = (this.productSearch || "").trim().toLowerCase();
      if (!q) {
        this.filteredProducts = [];
        this.showDropdown = false;
        return;
      }
      this.filteredProducts = this.products
        .filter((p) => {
          const code = String(p.code ?? "").toLowerCase();
          const name = String(p.name ?? "").toLowerCase();
          return code.includes(q) || name.includes(q);
        })
        .slice(0, 40);
      this.showDropdown = true;
      if (this.produtoSelecionado) {
        const label = `${this.produtoSelecionado.code} — ${this.produtoSelecionado.name}`;
        if (this.productSearch !== label && this.productSearch !== this.produtoSelecionado.code) {
          this.produtoSelecionado = null;
        }
      }
    },
    selectProduct(prod) {
      this.produtoSelecionado = prod;
      this.productSearch = `${prod.code} — ${prod.name}`;
      this.showDropdown = false;
      this.filteredProducts = [];
    },
    hideDropdown() {
      setTimeout(() => {
        this.showDropdown = false;
      }, 150);
    },
    formatNum(value, unidade) {
      if (value === null || value === undefined || Number.isNaN(Number(value))) return "—";
      const isUn = String(unidade || "").trim().toUpperCase() === "UN";
      return Number(value).toLocaleString("pt-BR", {
        minimumFractionDigits: isUn ? 0 : 2,
        maximumFractionDigits: isUn ? 0 : 2,
      });
    },
    flattenArvore(nos, nivel) {
      const out = [];
      for (const no of nos || []) {
        out.push({ ...no, nivel });
        if (no.filhos?.length) {
          out.push(...this.flattenArvore(no.filhos, nivel + 1));
        }
      }
      return out;
    },
    limparResultado() {
      this.resultado = null;
      this.apiError = null;
    },
    validar() {
      this.erroValidacao = null;
      if (this.tipoAtivo === TIPOS.insumos) {
        if (!this.produtoSelecionado?.id && !this.produtoSelecionado?.code) {
          this.erroValidacao = "Selecione um produto.";
          return false;
        }
        if (!this.qtMeta || this.qtMeta <= 0) {
          this.erroValidacao = "Informe uma quantidade meta maior que zero.";
          return false;
        }
        return true;
      }
      if (this.tipoAtivo === TIPOS.produto_ano) {
        if (!this.produtoSelecionado?.id && !this.produtoSelecionado?.code) {
          this.erroValidacao = "Selecione um produto.";
          return false;
        }
        if (!this.ano || String(this.ano).length !== 4) {
          this.erroValidacao = "Informe um ano com 4 dígitos.";
          return false;
        }
        return true;
      }
      if (!this.familiaId) {
        this.erroValidacao = "Selecione uma família.";
        return false;
      }
      if (!this.ano || String(this.ano).length !== 4) {
        this.erroValidacao = "Informe um ano com 4 dígitos.";
        return false;
      }
      return true;
    },
    async consultar() {
      if (!this.validar()) return;
      this.loading = true;
      this.apiError = null;
      this.resultado = null;
      try {
        if (this.tipoAtivo === TIPOS.insumos) {
          this.resultado = await MetasProducaoDatasource.insumosMeta({
            produto_cod: this.produtoSelecionado.id,
            cod: this.produtoSelecionado.code || undefined,
            qt_meta: this.qtMeta,
            modo: this.modo,
            considerar_estoque: this.considerarEstoque ? 1 : 0,
          });
        } else if (this.tipoAtivo === TIPOS.produto_ano) {
          this.resultado = await MetasProducaoDatasource.quantidadeAnoProduto({
            produto_cod: this.produtoSelecionado.id,
            cod: this.produtoSelecionado.code || undefined,
            ano: this.ano,
          });
        } else {
          this.resultado = await MetasProducaoDatasource.quantidadeAnoFamilia({
            familia_id: this.familiaId,
            ano: this.ano,
            retornar_produtos: this.retornarProdutos ? 1 : 0,
          });
        }
      } catch (e) {
        console.error(e);
        const msg =
          e?.response?.data?.message ||
          e?.response?.data?.error ||
          e?.message ||
          "Erro ao consultar.";
        this.apiError = typeof msg === "string" ? msg : "Erro ao consultar.";
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.kpi-abas {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
}

.kpi-abas__btn {
  border: 1px solid var(--cor-separador);
  background: var(--cor-bg);
  color: var(--cor-fonte);
  padding: 8px 14px;
  border-radius: 20px;
  cursor: pointer;
  font: inherit;
}

.kpi-abas__btn--ativa {
  background: var(--cor-primaria, #2c5aa0);
  color: #fff;
  border-color: transparent;
}

.metas-secao-titulo {
  margin: 0 0 12px;
  font-size: 1.05rem;
}

.metas-check {
  display: flex;
  align-items: flex-end;
  padding-bottom: 8px;
}

.metas-check label {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}

.metas-acoes {
  gap: 10px;
}

.metas-erro {
  color: var(--cor-erro);
  margin-top: 8px;
}

.metas-tile {
  padding: 12px 14px;
  border-radius: 8px;
  background: var(--cor-cinza);
}

.metas-tile__label {
  font-size: 0.8rem;
  color: var(--cor-fonte-fraca);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 6px;
}

.metas-tile__value {
  font-size: 1.4rem;
  font-weight: 700;
}

.autocomplete-wrapper {
  position: relative;
}

.autocomplete-list {
  position: absolute;
  z-index: 20;
  left: 0;
  right: 0;
  max-height: 240px;
  overflow: auto;
  margin: 0;
  padding: 0;
  list-style: none;
  background: var(--cor-bg, #fff);
  border: 1px solid var(--cor-separador);
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.autocomplete-list li {
  padding: 8px 10px;
  cursor: pointer;
}

.autocomplete-list li:hover {
  background: var(--cor-cinza);
}

.margem {
  margin-top: 16px;
}
</style>
